import { indexedDBManager } from "./system/indexed-db";
import { logger } from "../utils/logger";
import { isProjectRecordKey, projectMetadataKey } from "./documentWriteGuard";
import { projectIdOfKey } from "./projectStorageMigration";

/**
 * Registry-INDEPENDENT raw two-tier project-record sweep — Bombproof storage
 * foundation, step 3 (board 12-imgstore; approval-gate §6.3 / RESHAPE-3).
 *
 * Deletes EVERY storage record belonging to a project across BOTH tiers
 * (localStorage + the IndexedDB document store `stagecraft_storage`/`data_store`),
 * WITHOUT consulting any registry. The registry under-reclaims: the shipped
 * `deleteProjectData` removed only `project_<id>` + `project_<id>_state` via
 * ElementStorage — leaking `_space_*`, `_page_*`, `_collaboratorRegistry`,
 * `_memberRegistry`, and `_state_preempty` on every delete.
 *
 * Scope = keys that are EXACTLY `project_<id>` (metadata) OR start with
 * `project_<id>_` (state / space_* / page_* / registries / state_preempty …).
 * Project ids are UUIDs (fixed length), so this never bleeds into a sibling
 * project; and the reserved keys `stagecraft_projects` (the project registry) /
 * `stagecraft_current_project` begin with `stagecraft_`, so they are never matched.
 *
 * The dedicated asset database `stagecraft_assets` is a SEPARATE IndexedDB database
 * and is unreachable from `indexedDBManager` (which only ever touches `data_store`
 * in `stagecraft_storage`) — so a document-tier sweep never disturbs stored assets.
 * (Per-asset GC is disclosed-deferred; project-delete reclaims documents now —
 * approval-gate §Disclosed-deferred.)
 *
 * Fail-safe: a failure in either tier is logged and never throws — the other tier
 * is still swept. Both functions return the reclaimed keys per tier (loud + testable).
 */

export interface ProjectSweepResult {
  /** localStorage keys reclaimed. */
  localStorage: string[];
  /** IndexedDB document-store keys reclaimed (enumerated minus failed deletes). */
  indexedDB: string[];
  /** WR-685 D10: IndexedDB keys whose delete failed (observable, never swallowed). */
  failed?: string[];
  /** WR-685 D10: keys still present when the sweep re-read the store afterwards. */
  survivors?: string[];
}

// The ONE owner test for "this key belongs to that project" now lives with the
// document write guard, which applies the same rule at WRITE time (WR-685 D1).
export { isProjectRecordKey };

/**
 * WR-685 D10 — the death record goes FIRST: delete the project's metadata record
 * (both tiers) in its own transaction, BEFORE the rest is enumerated. From that commit
 * on, the document write guard refuses every write of this project in every tab; any
 * write that committed before it is visible to the enumeration that follows and swept.
 * Returns whether the IndexedDB delete succeeded.
 */
export async function deleteProjectMetadataRecord(
  projectId: string,
): Promise<boolean> {
  const key = projectMetadataKey(projectId);
  try {
    localStorage.removeItem(key);
  } catch {
    // localStorage unavailable — the IndexedDB record is the authority.
  }
  const result = await indexedDBManager.delete(key);
  if (!result.success) {
    logger.error("projectStorageSweep: metadata record delete FAILED", {
      error: result.error,
      data: { projectId },
    });
  }
  return result.success;
}

/** Enumerate the localStorage keys that satisfy `predicate` (sync, fail-safe). */
function localStorageKeysWhere(predicate: (key: string) => boolean): string[] {
  const matches: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key !== null && predicate(key)) matches.push(key);
    }
  } catch {
    // localStorage unavailable — nothing to sweep on this tier.
  }
  return matches;
}

/** Delete `keys` from localStorage (fail-safe per key). */
function deleteLocalStorageKeys(keys: string[]): void {
  for (const key of keys) {
    try {
      localStorage.removeItem(key);
    } catch {
      // Best-effort; a single failure must not abort the batch.
    }
  }
}

/**
 * Delete `keys` from the IndexedDB document store (fail-safe per key). Returns the
 * keys whose delete FAILED — WR-685 D10: a failure is recorded, never swallowed
 * (`indexedDBManager.delete` reports failure as a result, it does not throw).
 */
async function deleteIndexedDbKeys(keys: string[]): Promise<string[]> {
  const failed: string[] = [];
  for (const key of keys) {
    try {
      const r = await indexedDBManager.delete(key);
      if (!r?.success) failed.push(key);
    } catch {
      failed.push(key);
    }
  }
  return failed;
}

/**
 * Reclaim EVERY record for `projectId` across both tiers. Registry-independent.
 */
export async function sweepProjectRecords(
  projectId: string,
): Promise<ProjectSweepResult> {
  const belongs = (key: string) => isProjectRecordKey(key, projectId);

  // localStorage tier (sync enumerate → delete).
  const lsKeys = localStorageKeysWhere(belongs);
  deleteLocalStorageKeys(lsKeys);

  // IndexedDB document tier. getKeysByPrefix is itself fail-safe (→ [] if the DB is
  // unavailable), so the LS sweep above always completes regardless of IDB health.
  let idbKeys: string[] = [];
  let failed: string[] = [];
  let survivors: string[] = [];
  try {
    idbKeys = (await indexedDBManager.getKeysByPrefix(`project_${projectId}`)).filter(
      belongs,
    );
    failed = await deleteIndexedDbKeys(idbKeys);
    // WR-685 D10: the observation that the sweep completed is a re-read, not the
    // count of delete requests issued (R1-C-2/C-6).
    survivors = (
      await indexedDBManager.getKeysByPrefix(`project_${projectId}`)
    ).filter(belongs);
  } catch (error) {
    logger.error("projectStorageSweep: IndexedDB sweep failed (non-fatal)", {
      error: error instanceof Error ? error : new Error(String(error)),
      data: { projectId },
    });
  }

  const reclaimed = idbKeys.filter((k) => !failed.includes(k));
  if (failed.length > 0 || survivors.length > 0) {
    logger.error("projectStorageSweep: project records SURVIVED the sweep", {
      data: { projectId, failed, survivors },
    });
  }
  logger.info("projectStorageSweep: reclaimed project records", {
    data: {
      projectId,
      localStorageCount: lsKeys.length,
      indexedDBCount: reclaimed.length,
    },
  });

  return { localStorage: lsKeys, indexedDB: reclaimed, failed, survivors };
}

/**
 * What the boot orphan sweep found and LEFT IN PLACE (hotfix-sweep-s1).
 */
export interface OrphanSweepReport {
  /** Always true: the boot sweep deletes nothing (see {@link bootOrphanSweep}). */
  reportOnly: true;
  /** Keys the sweep would have deleted, per tier. Nothing was deleted. */
  wouldDelete: { localStorage: string[]; indexedDB: string[] };
  /** Distinct project ids owning those keys (projects missing from the list). */
  projectIds: string[];
}

/**
 * REPORT-ONLY (hotfix-sweep-s1, D1 containment, CTO 65-112): find `project_*`
 * records for projects NOT in `liveProjectIds`, log them, and DELETE NOTHING.
 *
 * WHY: `liveProjectIds` comes from the project list `stagecraft_projects`, and two
 * tabs rewriting that list can drop a live project from it. Deleting by list
 * membership therefore destroyed whole live projects at the next boot (storage-s1
 * C0). Until storage-s1's (d) design restores reclaim on a sound liveness rule, the
 * sweep only reports. Accepted cost: genuine orphans (and the asset blobs they
 * reference) are kept — see TECHNICAL-DEBT.md and the TR's Known Problems.
 *
 * ⚠️ ORDERING (unchanged): runs AFTER the one-time localStorage→IndexedDB migration
 * (NA-B2″). The localStorage keys it can see at boot are the ones migration
 * deliberately left in place (corrupt, or not durably copied) — for a project
 * missing from the list, its only copy.
 *
 * A key is an orphan iff it begins with `project_` (a project record — the reserved
 * `stagecraft_*` keys are excluded by construction) AND belongs to no live project.
 * The report is logged at ERROR because production logs nothing below it.
 */
export async function bootOrphanSweep(
  liveProjectIds: string[],
): Promise<OrphanSweepReport> {
  const belongsToLive = (key: string) =>
    liveProjectIds.some((id) => isProjectRecordKey(key, id));
  const isOrphan = (key: string) =>
    key.startsWith("project_") && !belongsToLive(key);

  // localStorage tier — enumerate only.
  const lsKeys = localStorageKeysWhere(isOrphan);

  // IndexedDB document tier — enumerate only.
  let idbKeys: string[] = [];
  try {
    idbKeys = (await indexedDBManager.getKeysByPrefix("project_")).filter(
      isOrphan,
    );
  } catch (error) {
    logger.error("bootOrphanSweep: IndexedDB enumeration failed (non-fatal)", {
      error: error instanceof Error ? error : new Error(String(error)),
    });
  }

  const projectIds = Array.from(
    new Set([...lsKeys, ...idbKeys].map(projectIdOfKey)),
  );

  if (lsKeys.length > 0 || idbKeys.length > 0) {
    logger.error(
      "bootOrphanSweep: REPORT-ONLY — records of projects NOT in the project list were left in place (nothing deleted)",
      {
        data: {
          localStorage: lsKeys,
          indexedDB: idbKeys,
          localStorageCount: lsKeys.length,
          indexedDBCount: idbKeys.length,
          projectIds,
          liveProjectCount: liveProjectIds.length,
        },
      },
    );
  }

  return {
    reportOnly: true,
    wouldDelete: { localStorage: lsKeys, indexedDB: idbKeys },
    projectIds,
  };
}
